import { useEffect, useMemo, useState } from 'react';
import { useTemplates } from '@/hooks/queries/useTemplates';
import { useIdentities } from '@/hooks/queries/useIdentities';
import { Stagger } from '@/components/primitives/Stagger';
import { TEMPLATE_PERIOD_OPTIONS, TEMPLATE_MIN_REELS } from '@/lib/constants';
import type { TemplateSortBy } from '@/types/api';
import { TemplatesHeader } from './TemplatesHeader';
import { TemplatesList } from './TemplatesList';
import { TemplatesKpiRow } from './TemplatesKpiRow';
import { TemplateDetailDialog } from './TemplateDetailDialog';

const PERIOD_STORAGE_KEY = 'templates:periodDays';
const SORT_STORAGE_KEY = 'templates:sortBy';
const DEFAULT_PERIOD = 30;
const DEFAULT_SORT: TemplateSortBy = 'MEDIAN_VIEWS';
const VALID_SORTS: TemplateSortBy[] = [
  'MEDIAN_VIEWS',
  'MAX_VIEWS',
  'REEL_COUNT',
  'VIRAL_RATE',
  'LAST_PUBLISHED_AT',
];

function loadPeriod(): number {
  if (typeof window === 'undefined') return DEFAULT_PERIOD;
  const raw = window.localStorage.getItem(PERIOD_STORAGE_KEY);
  const parsed = raw ? Number(raw) : NaN;
  // On contraint la valeur aux options UI (7/30/90/0) pour éviter un <Select> vide
  // si un ancien localStorage contient une valeur hors liste.
  const allowed = TEMPLATE_PERIOD_OPTIONS.map((o) => o.value) as number[];
  return allowed.includes(parsed) ? parsed : DEFAULT_PERIOD;
}

function loadSort(): TemplateSortBy {
  if (typeof window === 'undefined') return DEFAULT_SORT;
  const raw = window.localStorage.getItem(SORT_STORAGE_KEY);
  return VALID_SORTS.includes(raw as TemplateSortBy) ? (raw as TemplateSortBy) : DEFAULT_SORT;
}

export function TemplatesScreen() {
  const [periodDays, setPeriodDays] = useState<number>(loadPeriod);
  const [sortBy, setSortBy] = useState<TemplateSortBy>(loadSort);
  const [identityFilter, setIdentityFilter] = useState<string | 'all'>('all');
  const [openHash, setOpenHash] = useState<string | null>(null);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(PERIOD_STORAGE_KEY, String(periodDays));
    }
  }, [periodDays]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(SORT_STORAGE_KEY, sortBy);
    }
  }, [sortBy]);

  const params = useMemo(
    () => ({
      periodDays,
      minReels: TEMPLATE_MIN_REELS,
      sortBy,
      sortDir: 'DESC' as const,
      identityId: identityFilter === 'all' ? undefined : identityFilter,
    }),
    [periodDays, sortBy, identityFilter],
  );

  const templates = useTemplates(params);
  const identities = useIdentities();

  const list = templates.data ?? [];

  return (
    <Stagger className="min-w-0 space-y-5">
      <TemplatesHeader
        periodDays={periodDays}
        onPeriodChange={setPeriodDays}
        sortBy={sortBy}
        onSortByChange={setSortBy}
        identityFilter={identityFilter}
        onIdentityChange={setIdentityFilter}
        identities={identities.data}
        totalTemplates={list.length}
      />

      <TemplatesKpiRow templates={list} isLoading={templates.isLoading} />

      <TemplatesList
        templates={list}
        isLoading={templates.isLoading}
        error={templates.error}
        onRetry={() => templates.refetch()}
        onOpen={(h) => setOpenHash(h)}
        periodDays={periodDays}
      />

      <TemplateDetailDialog
        templateHash={openHash}
        periodDays={periodDays}
        open={!!openHash}
        onOpenChange={(o) => !o && setOpenHash(null)}
      />
    </Stagger>
  );
}
